import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateServiceRequestDto,
  ServiceRequestSummary,
  TransitionServiceRequestDto,
} from '@munifocus/contracts-gov';
import { api } from './api-client';

/** Which side the caller is acting for — the active scope. */
export type RequestSide = { kind: 'local_government' | 'company'; id: string };

const base = (side: RequestSide) =>
  side.kind === 'company'
    ? `/service-requests/company/${side.id}`
    : `/service-requests/local-government/${side.id}`;

export function useServiceRequests(side: RequestSide | null) {
  return useQuery({
    queryKey: ['service-requests', side?.kind, side?.id],
    queryFn: async () => (await api.get<ServiceRequestSummary[]>(base(side as RequestSide))).data,
    enabled: Boolean(side),
  });
}

export function useCreateServiceRequest(side: RequestSide | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateServiceRequestDto) =>
      (await api.post<ServiceRequestSummary>(base(side as RequestSide), dto)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-requests'] }),
  });
}

export function useTransitionServiceRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...dto }: TransitionServiceRequestDto & { id: string }) =>
      (await api.post<ServiceRequestSummary>(`/service-requests/${id}/transition`, dto)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-requests'] }),
  });
}
